/**
 * Balance: equals, felt in the arm of a scale.
 *
 * Seven and five on one side, twelve on the other, and the beam goes level.
 * The mystery box hides a number and the child has to work out what would make
 * it level again — which is solving an equation, done with hands.
 */

import { useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { Home, RotateCcw, Sparkles } from 'lucide-preact';
import type { Spark } from '../../engine/render.ts';
import { drawSparks, spawnSparks, updateSparks } from '../../engine/render.ts';
import { partyColors, token } from '../../engine/palette.ts';
import { play } from '../../engine/audio.ts';
import { useSurface } from '../../hooks/useSurface.ts';
import { Chip, Coach, ToyButton } from '../../ui/controls.tsx';

interface Props {
  onExit: () => void;
  tabs: ComponentChildren;
}

const WEIGHTS = [1, 2, 3, 5, 10, 25, 100];
const MAX_TILT = 0.34;

interface Pan {
  weights: number[];
  /** Hidden weight the child is trying to work out, or 0 when there is none. */
  mystery: number;
}

const sum = (pan: Pan): number => pan.weights.reduce((a, b) => a + b, 0) + pan.mystery;

export function BalanceToy({ onExit, tabs }: Props): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const sparksRef = useRef<Spark[]>([]);
  const tiltRef = useRef(0);
  const hitsRef = useRef<Array<{ side: 'left' | 'right'; index: number; x: number; y: number; r: number }>>([]);

  const [left, setLeft] = useState<Pan>({ weights: [], mystery: 0 });
  const [right, setRight] = useState<Pan>({ weights: [], mystery: 0 });
  const [pick, setPick] = useState(1);
  const [revealed, setRevealed] = useState(false);

  const live = useRef({ left, right, revealed });
  live.current = { left, right, revealed };

  const leftTotal = sum(left);
  const rightTotal = sum(right);
  const level = leftTotal === rightTotal && (leftTotal > 0 || rightTotal > 0);
  const hidden = left.mystery > 0 || right.mystery > 0;

  // Going level is the whole point, so it gets a proper celebration.
  const wasLevel = useRef(level);
  useEffect(() => {
    if (level && !wasLevel.current) {
      play('yay', 0.85);
      const canvas = canvasRef.current;
      if (canvas) {
        const rect = canvas.getBoundingClientRect();
        spawnSparks(sparksRef.current, { x: rect.width / 2, y: rect.height * 0.42 }, partyColors(), 30, 460);
      }
      if (hidden) setRevealed(true);
    }
    wasLevel.current = level;
  }, [level, hidden]);

  const addWeight = (side: 'left' | 'right') => {
    const setter = side === 'left' ? setLeft : setRight;
    setter((pan) => ({ ...pan, weights: [...pan.weights, pick] }));
    play('count', Math.min(1, pick / 12));
  };

  useSurface(canvasRef, {
    onFrame: (ctx, dt, { width, height }) => {
      updateSparks(sparksRef.current, dt);
      const state = live.current;
      const l = sum(state.left);
      const r = sum(state.right);

      // The beam leans by the difference, but only so far — a scale has stops.
      const heaviest = Math.max(l, r, 1);
      const target = Math.max(-1, Math.min(1, (r - l) / heaviest)) * MAX_TILT;
      tiltRef.current += (target - tiltRef.current) * (1 - Math.exp(-dt * 7));

      ctx.fillStyle = token('--paper');
      ctx.fillRect(0, 0, width, height);

      const pivotX = width / 2;
      const pivotY = height * 0.38;
      const arm = Math.min(width * 0.34, 190);
      const tilt = tiltRef.current;

      // Stand.
      ctx.save();
      ctx.strokeStyle = token('--ink');
      ctx.lineWidth = 8;
      ctx.lineCap = 'round';
      ctx.beginPath();
      ctx.moveTo(pivotX, pivotY);
      ctx.lineTo(pivotX, height * 0.78);
      ctx.moveTo(pivotX - 52, height * 0.78);
      ctx.lineTo(pivotX + 52, height * 0.78);
      ctx.stroke();

      // Beam.
      ctx.translate(pivotX, pivotY);
      ctx.rotate(tilt);
      ctx.strokeStyle = token('--crayon-purple');
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.moveTo(-arm, 0);
      ctx.lineTo(arm, 0);
      ctx.stroke();
      ctx.restore();

      const hits: typeof hitsRef.current = [];
      const panFor = (side: 'left' | 'right') => {
        const dir = side === 'left' ? -1 : 1;
        return {
          x: pivotX + Math.cos(tilt) * arm * dir,
          y: pivotY + Math.sin(tilt) * arm * dir,
        };
      };

      for (const side of ['left', 'right'] as const) {
        const anchor = panFor(side);
        const pan = side === 'left' ? state.left : state.right;
        const dishY = anchor.y + 74;

        ctx.save();
        ctx.strokeStyle = token('--ink-soft');
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(anchor.x, anchor.y);
        ctx.lineTo(anchor.x, dishY);
        ctx.stroke();
        ctx.strokeStyle = token('--ink');
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.arc(anchor.x, dishY, 54, 0.1 * Math.PI, 0.9 * Math.PI);
        ctx.stroke();
        ctx.restore();

        // Weights pile into the dish, newest on top.
        pan.weights.forEach((weight, i) => {
          const perRow = 4;
          const row = Math.floor(i / perRow);
          const col = i % perRow;
          const r = 17;
          const x = anchor.x - ((Math.min(pan.weights.length - row * perRow, perRow) - 1) * (r * 2 + 3)) / 2 + col * (r * 2 + 3);
          const y = dishY - 16 - row * (r * 2 + 2);
          drawWeight(ctx, x, y, r, String(weight), token('--crayon-teal'));
          hits.push({ side, index: i, x, y, r });
        });

        if (pan.mystery > 0) {
          drawWeight(
            ctx,
            anchor.x,
            dishY - 22,
            26,
            state.revealed ? String(pan.mystery) : '?',
            token(state.revealed ? '--crayon-green' : '--crayon-orange')
          );
        }
      }

      hitsRef.current = hits;
      drawSparks(ctx, sparksRef.current);
    },

    onUp: (p, { width }) => {
      // Tapping a weight in a dish takes it back off.
      const hit = hitsRef.current.find((h) => Math.hypot(p.pos.x - h.x, p.pos.y - h.y) <= h.r + 6);
      if (hit) {
        const setter = hit.side === 'left' ? setLeft : setRight;
        setter((pan) => ({ ...pan, weights: pan.weights.filter((_, i) => i !== hit.index) }));
        play('pop', 0.5);
        return;
      }
      addWeight(p.pos.x < width / 2 ? 'left' : 'right');
    },
  });

  const reset = () => {
    setLeft({ weights: [], mystery: 0 });
    setRight({ weights: [], mystery: 0 });
    setRevealed(false);
    play('whoosh', 0.6);
  };

  const puzzle = () => {
    const secret = 2 + Math.floor(Math.random() * 18);
    const helper = 1 + Math.floor(Math.random() * 9);
    setLeft({ weights: [helper], mystery: secret });
    setRight({ weights: [], mystery: 0 });
    setRevealed(false);
    play('sparkle', 0.7);
  };

  return (
    <div class="screen">
      <div class="stage">
        <canvas ref={canvasRef} aria-label="Balance scale" />
        <div class="topbar">
          <ToyButton round label="Home" hideLabel onClick={onExit}>
            <Home size={24} strokeWidth={2.5} />
          </ToyButton>
          <div class="topbar__group">
            <ToyButton round label="Hide a number in the scale" hideLabel onClick={puzzle}>
              <Sparkles size={24} strokeWidth={2.5} />
            </ToyButton>
            <ToyButton round label="Empty both sides" hideLabel onClick={reset}>
              <RotateCcw size={24} strokeWidth={2.5} />
            </ToyButton>
          </div>
        </div>

        <p class="readout">
          <strong>
            {leftTotal} {level ? '=' : leftTotal > rightTotal ? '>' : '<'} {rightTotal}
          </strong>
          <span>{level ? 'level!' : `${Math.abs(leftTotal - rightTotal)} more would do it`}</span>
        </p>

        <Coach>{hidden ? 'What is hiding in the box? Make it level to find out' : 'Tap a side to put a weight on it'}</Coach>
      </div>

      <div class="dock">
        {tabs}
        <div class="dock__row dock__row--scroll" role="group" aria-label="Weight to add">
          {WEIGHTS.map((n) => (
            <Chip key={n} label={String(n)} pressed={pick === n} onClick={() => setPick(n)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function drawWeight(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, label: string, color: string): void {
  ctx.save();
  ctx.fillStyle = color;
  ctx.strokeStyle = 'rgba(44, 37, 69, 0.8)';
  ctx.lineWidth = 2.5;
  ctx.beginPath();
  ctx.roundRect(x - r, y - r, r * 2, r * 2, r * 0.42);
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = '#ffffff';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = `800 ${r * 0.86}px ui-rounded, system-ui, sans-serif`;
  ctx.fillText(label, x, y);
  ctx.restore();
}
